/**
 * useDeviceOrganization - Refactored to use OrganizationCache
 * 
 * Eliminates direct database queries by using centralized cache
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { usePrimaryDevice } from './usePrimaryDevice';
import { useDevicePWAConfig } from './useDevicePWAConfig';
import { useTheme } from 'next-themes';

interface DeviceOrganization {
  organizationId: string | null;
  organizationName: string | null;
  organizationSlug: string | null;
  logoUrl: string | null;
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  defaultTheme: 'light' | 'dark' | null;
  // Seller/merchant identity (public business details, shown before purchase)
  orgNumber: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  loading: boolean;
}

export function useDeviceOrganization(deviceId?: string): DeviceOrganization {
  const { primaryDevice, loading: loadingDevice } = usePrimaryDevice();
  const { resolvedTheme } = useTheme();
  // Use provided deviceId or fall back to primary device
  const effectiveDeviceId = deviceId || primaryDevice?.id;
  
  // Get PWA config which includes logo URLs
  const { config, isLoading: configLoading } = useDevicePWAConfig(effectiveDeviceId);

  const { data, isLoading } = useQuery({
    queryKey: ['device-organization', effectiveDeviceId],
    queryFn: async () => {
      if (!effectiveDeviceId) {
        return null;
      }

      // Public RPC that returns safe branding data for this device (works for guests)
      const { data: branding, error: brandingError } = await supabase.rpc('get_device_org_branding', {
        p_device_id: effectiveDeviceId,
      });

      if (brandingError || !branding) {
        return null;
      }

      const org = Array.isArray(branding) ? branding[0] : branding;
      if (!org?.organization_id) {
        return null;
      }

      return {
        organizationId: org.organization_id,
        organizationName: org.organization_name,
        orgSlug: org.organization_slug,
        orgLogoLight: org.logo_light_url ?? null,
        orgLogoDark: org.logo_dark_url ?? null,
        defaultTheme: (org as any).default_theme ?? null,
        orgNumber: (org as any).org_number ?? null,
        address: (org as any).address ?? null,
        postalCode: (org as any).postal_code ?? null,
        city: (org as any).city ?? null,
        phone: (org as any).phone ?? null,
        email: (org as any).email ?? null,
      };
    },
    enabled: !!effectiveDeviceId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Prioritize PWA config logos, fallback to organization_settings logos
  const logoLightUrl = (config as any)?.logo_light_url || data?.orgLogoLight || null;
  const logoDarkUrl = (config as any)?.logo_dark_url || data?.orgLogoDark || null;
  const logoUrl = resolvedTheme === 'dark' ? logoDarkUrl : logoLightUrl;

  return {
    organizationId: data?.organizationId || null,
    organizationName: data?.organizationName || null,
    organizationSlug: data?.orgSlug || null,
    logoUrl: logoUrl || logoLightUrl || logoDarkUrl,
    logoLightUrl,
    logoDarkUrl,
    defaultTheme: data?.defaultTheme as 'light' | 'dark' | null ?? null,
    orgNumber: data?.orgNumber ?? null,
    address: data?.address ?? null,
    postalCode: data?.postalCode ?? null,
    city: data?.city ?? null,
    phone: data?.phone ?? null,
    email: data?.email ?? null,
    loading: loadingDevice || isLoading || configLoading
  };
}
